import { Box, Card, CardContent, Grid2, Stack, Typography, useTheme } from "@mui/material";
import PageTitle from "../components/PageTitle";
import { InternalLink } from "../../shared/components/InternalLink";

interface ResourceCardProps {
  title: string;
  description: string;
  href: string;
  icon: string;
  external?: boolean;
}

function ResourceCard({ title, description, href, icon, external }: ResourceCardProps) {
  const theme = useTheme();
  const linkProps = external
    ? { href, target: "_blank", rel: "noopener noreferrer" }
    : { href };

  return (
    <Card
      component="a"
      {...linkProps}
      sx={{
        background: theme.palette.background.paper,
        height: "100%",
        textDecoration: "none",
        display: "block",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 8px 24px rgba(140, 0, 255, 0.3)`,
        },
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          <Box sx={{ fontSize: "2rem" }}>{icon}</Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {description}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

const resources: ResourceCardProps[] = [
  {
    title: "Documentation",
    description:
      "Complete guides, tutorials, and API reference to help you build your first game.",
    href: "https://pretty-chitty.github.io/pretty-chitty/",
    icon: "📚",
    external: true,
  },
  {
    title: "GitHub",
    description:
      "Browse the source code, report issues, and contribute to the Pretty Chitty engine.",
    href: "https://github.com/pretty-chitty/pretty-chitty",
    icon: "🐙",
    external: true,
  },
  {
    title: "@pretty-chitty/core",
    description:
      "The core TypeScript package for building games. Install via npm to get started.",
    href: "https://www.npmjs.com/package/@pretty-chitty/core",
    icon: "📦",
    external: true,
  },
  {
    title: "@pretty-chitty/cli",
    description:
      "Command-line tools for scaffolding, testing, and deploying your games.",
    href: "https://www.npmjs.com/package/@pretty-chitty/cli",
    icon: "⌨️",
    external: true,
  },
  {
    title: "Demo Game",
    description:
      "Try a sample game to see Pretty Chitty in action. Great for understanding the player experience.",
    href: "/spa/game",
    icon: "🎲",
    external: false,
  },
];

export default function Develop() {
  const theme = useTheme();
  return (
    <Box>
      <PageTitle
        title="Develop"
        subTitle="Build your own online board games"
      />

      {/* Hero section */}
      <Box
        sx={{
          background: theme.palette.background.paper,
          borderRadius: 3,
          p: 4,
          mb: 4,
        }}
      >
        <Grid2 container spacing={4} alignItems="center">
          <Grid2 size={{ xs: 12, md: 7 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              Free & Open Source
            </Typography>
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
              Pretty Chitty is a free TypeScript package that makes building online
              board games incredibly fast. Focus on your game logic while we handle
              rendering, networking, state management, and cross-platform compatibility.
            </Typography>
            <Box
              component="pre"
              sx={{
                background: "#1a0338",
                p: 2,
                borderRadius: 2,
                overflow: "auto",
                fontSize: "0.9rem",
              }}
            >
              <code>npm install @pretty-chitty/core @pretty-chitty/cli</code>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 5 }}>
            <Box
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.secondary.main}33 0%, ${theme.palette.primary.main}33 100%)`,
                borderRadius: 2,
                p: 3,
                border: `1px solid ${theme.palette.secondary.main}55`,
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Quick Start
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2">1. Install the packages</Typography>
                <Typography variant="body2">2. Run <code>pretty-chitty init</code></Typography>
                <Typography variant="body2">3. Define your game logic</Typography>
                <Typography variant="body2">4. Test locally with hot reload</Typography>
                <Typography variant="body2">5. Deploy and play!</Typography>
              </Stack>
            </Box>
          </Grid2>
        </Grid2>
      </Box>

      {/* Resources */}
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Resources
      </Typography>
      <Grid2 container spacing={3} sx={{ mb: 5 }}>
        {resources.map((resource) => (
          <Grid2 key={resource.title} size={{ xs: 12, sm: 6, md: 4 }}>
            <ResourceCard {...resource} />
          </Grid2>
        ))}
      </Grid2>

      {/* License section */}
      <Box
        sx={{
          background: "#1a0338",
          borderRadius: 3,
          p: 4,
          border: `1px solid rgba(255, 255, 255, 0.1)`,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          License
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
          Pretty Chitty is free for personal use and non-commercial projects. Developers
          can use it freely to create games for themselves and their communities.
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
          <strong style={{ color: theme.palette.text.primary }}>Commercial use is restricted</strong>{" "}
          with one exception: you may use Pretty Chitty commercially to promote the sale
          of physical board games. This means publishers can create digital versions of
          their tabletop games to help customers learn and try before they buy.
        </Typography>
      </Box>

      {/* CTA */}
      <Box sx={{ mt: 5, textAlign: "center" }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Questions? Want to showcase your game?
        </Typography>
        <InternalLink to="/game">Browse existing games for inspiration</InternalLink>
      </Box>
    </Box>
  );
}
